// A bounce or complaint rate, coloured by the zone rule 4 puts it in, with the
// sentence that says why on hover and for a screen reader — and, when there is
// a trend, the sparkline beside it with the raise line dashed.
//
// The colour comes from components/emc/thresholds.ts and nowhere else. A dot
// goes with it, so the state does not rest on hue alone; a rate too small for
// the rule to judge is grey, whatever it is.

import { Sparkline } from "@/components/emc/charts/Sparkline";
import type { Tone } from "@/components/emc/status";
import { PROVIDER_NOTE, percentText, verdictText } from "@/components/emc/thresholds";
import type { RateVerdict } from "@/components/emc/thresholds";
import { TONE_FILL, TONE_TEXT } from "@/components/emc/tone";
import { cn } from "@/lib/utils";

// A rate under the raise line is ordinary text with no dot; a crossed line
// says so in the tone's ink and with its dot.
function textOf(tone: Tone): string {
  return tone === "muted" ? "" : cn(TONE_TEXT[tone], "font-semibold");
}

function dotOf(tone: Tone): string {
  return tone === "muted" ? "bg-transparent" : TONE_FILL[tone];
}

interface Props {
  verdict: RateVerdict;
  /** The sample the rule needs here: 200 for a pool's day, 20 for a provider. */
  minSample: number;
  /** Oldest first; null where nothing was sent. */
  trend?: (number | null)[];
  trendTone?: Tone;
  /** One provider's row: the lines are the pool's, and crossing one stops nothing. */
  provider?: boolean;
  /** Said after the verdict: a range row's worst day, which its colour follows. */
  note?: string | null;
  className?: string;
}

export function RuleRate({ verdict, minSample, trend, trendTone, provider = false, note, className }: Props) {
  const crossed = !verdict.small && (verdict.zone === "over" || verdict.zone === "between");
  const sentence =
    verdictText(verdict, minSample) + (provider && crossed ? `; ${PROVIDER_NOTE}` : "") + (note ? `; ${note}` : "");
  return (
    <span className={cn("inline-flex items-center justify-end gap-2", className)}>
      <span
        title={sentence}
        className={cn(
          "inline-flex items-center gap-1.5 tabular-nums",
          textOf(verdict.tone),
          verdict.small && "text-muted-foreground",
        )}
      >
        <span aria-hidden="true" className={cn("size-1.5 shrink-0 rounded-full", dotOf(verdict.tone))} />
        {percentText(verdict.percent)}
        <span className="sr-only">. {sentence}</span>
      </span>
      {trend && (
        <Sparkline
          values={trend}
          threshold={verdict.raiseLine}
          tone={trendTone ?? "running"}
          ariaLabel={`${verdict.metric === "bounce" ? "bounce" : "panasz"}, ${trend.length} nap`}
          format={(value) => percentText(value)}
          className="h-5 w-16"
        />
      )}
    </span>
  );
}
